import { createV3Queries } from "@query/utils/createV3Queries";
import { signatureRequestsApi } from "@api";
import { WSQueryKey } from "@ws-react-query";
import { getRequirementsDependencies } from "../../modules/Requirements/dependencies";

/**
 * Signature-request registry — mirrors `SignatureRequestsApi` 1:1
 * (`/v3/compliance/signature-requests`; create lives on the vault-file path).
 * Signature requests back `Signature` requirement instances, so every write
 * fans out into the requirement reads (`REQUIREMENTS_DEPENDENCIES`), lazily to
 * stay decoupled from sibling registries' module-init order.
 *
 * The mutated request is additionally *awaited*: the requirement configs
 * resolve their status from the cached document
 * (`getSignatureRequestFromCache`), so a flow that finishes before the refetch
 * lands leaves the screen on the pre-write status.
 *
 * Awaited keys stay EXACT — `awaitDependencies` resolves through
 * `refetchQueries`, which is prefix-matching and includes inactive queries, so a
 * `queryKeyBase` here would sequentially refetch every cached signature request
 * before the mutation resolves.
 */
const signatureVerbConfig = (queries: {
  getSignatureRequest: {
    getQueryKey: (request: { requestId: string }) => WSQueryKey;
  };
}) => ({
  dependencies: () => getRequirementsDependencies(),
  awaitDependencies: (_response: unknown, request: { requestId: string }) => [
    queries.getSignatureRequest.getQueryKey({ requestId: request.requestId })
  ]
});

export const queriesSignatureRequests = createV3Queries(
  "signatureRequestsApi",
  signatureRequestsApi,
  {
    // A create has no pre-existing document to await — only the fan-out.
    createSignatureRequest: () => ({
      dependencies: () => getRequirementsDependencies()
    }),
    cancelSignatureRequest: signatureVerbConfig,
    remindSignatureRequest: signatureVerbConfig
  }
);
